import { logAmplitudeEvent } from 'amplitude/amplitude'
import { clearLineItems, orderSelectors } from 'ducks/orderComponents'
import { permissionsSelectors } from 'ducks/permissions'
import BannerAd from 'elements/BannerAd'
import { LEADERBOARD_FORMATS } from 'elements/ads/AdPlacements'
import type { Filter } from 'elements/controller/filter/filter'
import useFilter from 'elements/controller/filter/useFilter'
import useQuery from 'elements/controller/query/useQuery'
import useListController from 'elements/controller/useListController'
import { Box, Button, styled, Typography } from 'opensolar-ui'
import { memo, useCallback, useMemo } from 'react'
import { useTranslate } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import ShopPageContainer from '../elements/ShopPageContainer'
import type { HardwareOrderProjectRecord } from '../type'
import TableContainer from './table/TableContainer'
import ProjectTableContent from './table/TableContent'
import ProjectTableHead from './table/TableHeader'
import ProjectTableToolbar from './toolbar/Toolbar'
import useProjectTable from './useProjectTable'

const PageContainer = styled(Box)<{ hasFooter?: boolean }>(({ hasFooter }) => ({
  paddingBottom: hasFooter ? 100 : 0,
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
}))

const HeaderSection = styled(Box)({
  marginBottom: '24px',
  width: '100%',
  minWidth: 0,
})

const MainTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 500,
  color: theme.palette.grey[900],
  marginBottom: '8px',
}))

const SubTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.grey[600],
  marginBottom: '0',
}))

const ToolbarSection = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '16px',
  gap: '16px',
  width: '100%',
  minWidth: 0,
})

const TableWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '8px',
  width: '100%',
  minWidth: 0,
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${theme.palette.grey[200]}`,
  overflow: 'hidden',
}))

const FooterSection = styled(Box)(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: '12px',
  padding: '16px 24px',
  backgroundColor: theme.palette.common.white,
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  zIndex: 1000,
}))

const ReviewButton = styled(Button)<{ disabled?: boolean }>(({ theme, disabled }) => ({
  backgroundColor: disabled ? `${theme.palette.grey[300]} !important` : `${theme.palette.blue[700]} !important`,
  color: disabled ? `${theme.palette.grey[500]} !important` : `${theme.palette.common.white} !important`,
  cursor: disabled ? 'not-allowed' : 'pointer',
  '&:hover': {
    backgroundColor: disabled ? `${theme.palette.grey[300]} !important` : `${theme.palette.blue[800]} !important`,
  },
}))

const defaultQuery = { perPage: 10 }
const defaultInitialFilter = {
  filterValues: {
    fieldset: 'hardware_ordering',
    ordering: 'system_sold_id',
    exclude_order_status: 'ordered',
  },
  displayedFilters: {},
}

const ProjectTable = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const translate = useTranslate()
  const location = useLocation<{ defaultFilter?: Filter }>()
  const shopHardwareAccess = useSelector((state) =>
    permissionsSelectors.getPermissionByKey(state, 'shop_hardware_access')
  )
  const allowEdit = shopHardwareAccess?.allowEdit ?? false
  const selectedProjectIds = useSelector(orderSelectors.getOrderProjectIds)
  const selectedCount = selectedProjectIds.length
  const [projectTableStore, projectTablePresenter] = useProjectTable()

  const initialFilter = useMemo(
    () => (location.state?.defaultFilter != null ? location.state?.defaultFilter : defaultInitialFilter),
    []
  )
  const { query, queryPresenter } = useQuery(defaultQuery)
  const { filter, filterPresenter } = useFilter({ defaultFilter: initialFilter, queryPresenter })
  const {
    data,
    ids,
    loaded,
    loading,
    total = 0,
  } = useListController<HardwareOrderProjectRecord>({
    resource: 'projects___aliases',
    query,
    filter,
  })

  const tableData = useMemo(() => ids.map((id) => data[id]).filter((a) => !!a), [ids, data])

  const handleAddToCart = useCallback(() => {
    logAmplitudeEvent(
      'generic_button_clicked',
      {
        source: 'review_cart_button',
        selectedProjectCount: selectedCount,
      },
      'Hardware Navigation Button'
    )
    history.push('/multi_project_ordering/review_cart')
  }, [history, logAmplitudeEvent, selectedCount])

  const handleReviewCart = useCallback(() => {
    history.push('/shop/cart')
  }, [history])

  const handleGoToCheckout = useCallback(() => {
    history.push('/checkout')
  }, [history])

  const handleCancel = useCallback(() => {
    dispatch(clearLineItems())
  }, [dispatch])

  const handleSelectAll = useCallback(() => {
    tableData.forEach((record) => {
      if (record.systems && record.systems.length > 0 && !selectedProjectIds.includes(record.id)) {
        const systemOptions = projectTablePresenter.getSystemSelectOptions(record.systems)
        const selectedSystemId = projectTablePresenter.getSystemSelectedId({
          projectId: record.id,
          soldSystemId: record.system_sold_id,
          selectedSystemMap: projectTableStore.selectedSystemMap,
          systemOptions,
        })
        if (selectedSystemId) {
          projectTablePresenter.onToggle({
            projectId: record.id,
            systemId: selectedSystemId,
            record,
          })
        }
      }
    })
  }, [tableData, selectedProjectIds, projectTablePresenter, projectTableStore.selectedSystemMap])

  const handleDeselectAll = useCallback(() => {
    dispatch(clearLineItems())
  }, [dispatch])

  const handleSort = useCallback(
    (sortField: string) => {
      const currentOrdering = filter.filterValues.ordering as string | undefined
      const isCurrentlyDesc = currentOrdering?.startsWith('-')
      const currentField = currentOrdering?.startsWith('-') ? currentOrdering.slice(1) : currentOrdering

      let newOrdering: string
      if (currentField === sortField) {
        newOrdering = isCurrentlyDesc ? sortField : `-${sortField}`
      } else {
        newOrdering = sortField
      }

      filterPresenter.setFilters(
        {
          ...filter.filterValues,
          ordering: newOrdering,
        },
        filter.displayedFilters,
        true
      )
    },
    [filter, filterPresenter, tableData]
  )

  const hasFilters = useMemo(() => {
    const excludedKeys = ['fieldset', 'ordering', 'exclude_order_status', 'q']
    return Object.keys(filter.filterValues).filter((key) => !excludedKeys.includes(key)).length > 0
  }, [filter.filterValues])

  const hasProjectWithoutSystemOption = useMemo(() => {
    return selectedProjectIds.some((projectId) => {
      const project = data[projectId]
      return project && (!project.systems || project.systems.length === 0)
    })
  }, [selectedProjectIds, data])

  const isReviewCartDisabled = hasProjectWithoutSystemOption

  return (
    <ShopPageContainer disableVerticalPadding>
      <PageContainer hasFooter={false}>
        <HeaderSection>
          <MainTitle>{translate('Multi-Project Ordering')}</MainTitle>
          <SubTitle>
            {translate(
              'Select one or more projects to add components from your designs to your BOM and purchase for multiple projects at once.'
            )}
          </SubTitle>
        </HeaderSection>

        <ToolbarSection>
          <ProjectTableToolbar resource={'projects'} filter={filter} filterPresenter={filterPresenter} />
        </ToolbarSection>

        <TableWrapper>
          <TableContainer
            loaded={loaded}
            loading={loading}
            total={total}
            query={query}
            queryPresenter={queryPresenter}
            hasFilters={hasFilters}
          >
            <ProjectTableHead
              data={tableData}
              selectedProjectIds={selectedProjectIds}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
              currentOrdering={filter.filterValues.ordering as string | undefined}
              onSort={handleSort}
            />
            <ProjectTableContent
              data={tableData}
              projectTableStore={projectTableStore}
              projectTablePresenter={projectTablePresenter}
              allowEdit={allowEdit}
            />
          </TableContainer>
        </TableWrapper>
        <BannerAd
          formats={LEADERBOARD_FORMATS}
          placement={`shop_multi_project_ordering`}
          containerStyle={{ marginTop: 10, marginBottom: 20 }}
          requiredWindowSize={{ leaderboard: { width: 960, height: 800 }, mobileLeaderboard: { height: 800 } }}
        />
      </PageContainer>
    </ShopPageContainer>
  )
}

export default memo(ProjectTable)
